// One take cut out of the rolling capture (issue #3722). Sing in Solfège keeps the singer's last take
// so "Hear mine" can play it back: when a take ends, the page takes the capture (the last 30 s of
// microphone, learn_mic_capture.ts) and keeps only the take's span of the microphone channel, in its
// own memory, for LearnAudioSession.playRecording. The guide reference channel is left behind: it is
// what the speaker played, not what the singer sang. Pure, so it is tested without audio.

import { CaptureRecording } from './learn_mic_capture.ts';

// A span of the microphone, ready to play: `startMs` is the audio-clock time of `samples[0]`, which
// is later than the span asked for when the ring no longer reached back that far, so a playhead
// drawn over the take can start where the audio does
export interface CaptureSlice {
  samples: Float32Array;
  sampleRate: number;
  startMs: number;
}

// The microphone's samples between `startMs` and `endMs` on the audio clock, a copy, clamped to what
// the recording holds. Undefined when the span is empty or none of it is in the recording (older
// than the ring, or not reached yet). A time maps to a sample as (t - startTimeMs) x sampleRate /
// 1000, rounded to the nearest sample.
export function sliceCapture(recording: CaptureRecording, startMs: number, endMs: number): CaptureSlice | undefined {
  const { samples, sampleRate, startTimeMs } = recording;
  const indexAt = (timeMs: number) => Math.round(((timeMs - startTimeMs) * sampleRate) / 1000);
  const first = Math.max(0, indexAt(startMs));
  const end = Math.min(samples.length, indexAt(endMs));
  if (!(end > first)) {
    return undefined; // empty, outside the recording, or not a number at all
  }
  return {
    samples: samples.slice(first, end),
    sampleRate,
    startMs: startTimeMs + (first * 1000) / sampleRate,
  };
}
